import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Button, Upload, message, Modal, Space, Typography, Avatar } from 'antd';
import { CameraOutlined, UploadOutlined, DeleteOutlined, UserOutlined } from '@ant-design/icons';
import { RcFile } from 'antd/lib/upload';

const { Text } = Typography;

interface PhotoCaptureProps {
    value?: string;
    onChange?: (file: File | null) => void;
    disabled?: boolean;
    hidePlaceholder?: boolean;
    showPreview?: boolean;
}

const PhotoCapture: React.FC<PhotoCaptureProps> = ({ value, onChange, disabled = false, hidePlaceholder = false, showPreview = true }) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [capturedImage, setCapturedImage] = useState<string | null>(value || null);
    const [isCameraActive, setIsCameraActive] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const streamRef = useRef<MediaStream | null>(null);

    const startCamera = useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    width: { ideal: 640 },
                    height: { ideal: 480 },
                    facingMode: 'user'
                }
            });
            
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                streamRef.current = stream;
                setIsCameraActive(true);
            }
        } catch (error) {
            console.error('Error accessing camera:', error);
            message.error('Unable to access camera. Please check permissions.');
        }
    }, []);

    const stopCamera = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        setIsCameraActive(false);
    }, []);

    const capturePhoto = useCallback(() => {
        if (videoRef.current && canvasRef.current) {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');

            if (context) {
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                context.drawImage(video, 0, 0);

                canvas.toBlob((blob) => {
                    if (blob) {
                        const file = new File([blob], 'member-photo.jpg', { type: 'image/jpeg' });
                        const imageUrl = URL.createObjectURL(blob);
                        setCapturedImage(imageUrl);
                        onChange?.(file);
                        stopCamera();
                        setIsModalVisible(false);
                        message.success('Photo captured successfully!');
                    }
                }, 'image/jpeg', 0.8);
            }
        }
    }, [onChange, stopCamera]);

    const handleFileUpload = (file: RcFile) => {
        const isImage = file.type.startsWith('image/');
        if (!isImage) {
            message.error('You can only upload image files!');
            return false;
        }

        const isLt5M = file.size / 1024 / 1024 < 5;
        if (!isLt5M) {
            message.error('Image must be smaller than 5MB!');
            return false;
        }

        const imageUrl = URL.createObjectURL(file);
        setCapturedImage(imageUrl);
        onChange?.(file);
        message.success('Photo uploaded successfully!');
        return false; // Prevent default upload behavior
    };

    const handleRemovePhoto = () => {
        setCapturedImage(null);
        onChange?.(null);
        message.success('Photo removed');
    };

    const showCameraModal = () => {
        setIsModalVisible(true);
        startCamera();
    };

    const handleModalCancel = () => {
        stopCamera();
        setIsModalVisible(false);
    };

    useEffect(() => {
        return () => {
            stopCamera();
            if (capturedImage && capturedImage.startsWith('blob:')) {
                URL.revokeObjectURL(capturedImage);
            }
        };
    }, [stopCamera, capturedImage]);

    return (
        <div>
            <Space direction="vertical" style={{ width: '100%' }}>
                {capturedImage && showPreview ? (
                    <div style={{ textAlign: 'center' }}>
                        <Avatar 
                            size={120} 
                            src={capturedImage}
                            icon={<UserOutlined />}
                            style={{ marginBottom: 16 }}
                        />
                        <br />
                        <Space wrap style={{ justifyContent: 'center' }}>
                            <Button
                                icon={<CameraOutlined />} 
                                onClick={showCameraModal}
                                disabled={disabled}
                            >
                                Retake Photo
                            </Button>
                            <Button 
                                icon={<DeleteOutlined />} 
                                onClick={handleRemovePhoto}
                                disabled={disabled}
                                danger
                            >
                                Remove Photo
                            </Button>
                        </Space>
                    </div>
                ) : (
                    <div style={{ textAlign: 'center' }}>
                        {!hidePlaceholder && (
                            <Avatar 
                                size={120} 
                                icon={<UserOutlined />}
                                style={{ marginBottom: 16 }}
                            />
                        )}
                        <Space wrap style={{ justifyContent: 'center' }}>
                            <Button
                                icon={<CameraOutlined />} 
                                onClick={showCameraModal}
                                disabled={disabled}
                                type="primary"
                            >
                                Take Photo
                            </Button>
                            <Upload
                                accept="image/*"
                                beforeUpload={handleFileUpload}
                                showUploadList={false}
                                disabled={disabled}
                            >
                                <Button icon={<UploadOutlined />} disabled={disabled}>
                                    Upload Photo
                                </Button>
                            </Upload>
                        </Space>
                    </div>
                )}
            </Space>

            <Modal
                title="Take Photo"
                open={isModalVisible}
                onCancel={handleModalCancel}
                footer={[
                    <Button key="cancel" onClick={handleModalCancel}>
                        Cancel
                    </Button>,
                    <Button 
                        key="capture" 
                        type="primary" 
                        onClick={capturePhoto}
                        disabled={!isCameraActive}
                    >
                        Capture Photo
                    </Button>
                ]}
                width={700}
            >
                <div style={{ textAlign: 'center' }}>
                    <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        style={{ 
                            width: '100%', 
                            maxWidth: '640px',
                            border: '1px solid #d9d9d9',
                            borderRadius: '8px'
                        }}
                    />
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                    {!isCameraActive && (
                        <div style={{ marginTop: 16 }}>
                            <Text type="secondary">Initializing camera...</Text>
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default PhotoCapture; 